/**
 * Which models can serve which video operation.
 *
 * Shared by the operation dock (what to offer) and the model picker (what to list), so
 * the two can't disagree — an operation on offer with no model behind it spends credits
 * on a clip that ignores its inputs.
 */

import type {
  VideoModelInfo,
  VideoOperationType,
  VideoProvidersCatalogResponse,
} from '../../../domain';

/** The catalog capability flag a given operation requires of a model. */
export function requiredCapability(kind: VideoOperationType): keyof VideoModelInfo | null {
  switch (kind) {
    case 'ImageToVideo':
      return 'supportsImageToVideo';
    case 'Interpolate':
      return 'supportsFirstLastFrame';
    // Extend and Restyle are separate fal endpoints served by different models, so they
    // ask for separate flags; a single "video to video" said nothing about which edit.
    case 'Extend':
      return 'supportsVideoExtend';
    case 'Restyle':
      return 'supportsVideoRestyle';
    case 'TextToVideo':
      return 'supportsTextToVideo';
    default:
      return null;
  }
}

export function modelSupports(model: VideoModelInfo, kind: VideoOperationType): boolean {
  const capability = requiredCapability(kind);
  if (!capability) return true;
  return Boolean(model[capability]);
}

/** Every model offered by a provider the catalog reports as available. */
export function availableModels(
  catalog: VideoProvidersCatalogResponse | undefined,
): VideoModelInfo[] {
  if (!catalog) return [];
  return catalog.video.filter(p => p.available).flatMap(p => p.models);
}

/** Whether any available model can serve `kind`. */
export function hasEligibleModel(
  catalog: VideoProvidersCatalogResponse | undefined,
  kind: VideoOperationType,
): boolean {
  return availableModels(catalog).some(model => modelSupports(model, kind));
}
